/**
 * Shared visual language for the Connections page's connector cards.
 *
 * Extracted from Integrations.tsx when Google Sheets became the second
 * connector: two cards sitting one above the other must not drift into two
 * slightly different greens, two slightly different "4 min ago" formats, or
 * two different shapes for the same status. Both cards import from here, so
 * consistency is structural rather than something to remember.
 *
 * Nothing here was redesigned in the move — the tokens, the pill shape and the
 * rounding rules are exactly what the HubSpot card already shipped with, along
 * with the reasoning behind each.
 */
import React from 'react';
import { Box, Typography } from '@mui/material';

/** Polled while a sync is actively running so progress updates without the
 *  tenant refreshing the page. Stopped the moment nothing is moving — this is
 *  a light status GET, not worth polling against an idle connector. */
export const CONNECTOR_POLL_MS = 4000;

export const STATUS_META: Record<
  string,
  { label: string; dot: string; text: string; bg: string }
> = {
  connected: { label: 'Connected', dot: '#1B8A5A', text: '#1B8A5A', bg: 'rgba(27,138,90,0.09)' },
  reauth_required: {
    label: 'Reconnect needed',
    dot: '#B7791F',
    text: '#8A5A00',
    bg: 'rgba(183,121,31,0.1)',
  },
  error: { label: 'Error', dot: '#C0392B', text: '#C0392B', bg: 'rgba(192,57,43,0.09)' },
};

/** Soft-background trigger chips instead of a plain outline — a quick visual
 *  read of "what kind of activity is this" at a glance in a history list, in
 *  the same soft-pill language as StatusPill rather than a heavier bordered
 *  chip. */
export const TRIGGER_CHIP_SX: Record<string, { bgcolor: string; color: string }> = {
  // Deliberately NOT orange/red: backfill is the routine "first import"
  // activity, but a warm orange-red pill in a status list reads as an error at
  // a glance regardless of the label next to it. Teal keeps it clearly
  // distinct from webhook/reconcile while staying unambiguously non-alarming —
  // red and amber stay reserved for genuine failures.
  backfill: { bgcolor: 'rgba(0,137,123,0.12)', color: '#00695C' },
  webhook: { bgcolor: 'rgba(124,77,255,0.12)', color: '#5E35B1' },
  // A sheet poll is recorded as a reconcile, so this is the chip a Google
  // Sheets history shows most; it reads as routine, which it is.
  reconcile: { bgcolor: 'rgba(2,136,209,0.1)', color: '#0277BD' },
  manual: { bgcolor: 'rgba(97,97,97,0.1)', color: '#616161' },
};

/** Human labels for the raw trigger enum — "reconcile" is an implementation
 *  word, and a tenant reading their own sync history should not have to learn
 *  it to understand that a scheduled check happened. */
export const TRIGGER_LABEL: Record<string, string> = {
  backfill: 'First import',
  webhook: 'Live update',
  reconcile: 'Scheduled check',
  manual: 'Manual',
};

/** A quiet dot + label instead of a heavy filled Chip — the "connected" state
 *  especially should read as calm, not like an alert badge, matching how
 *  Linear/Vercel/GitHub show a healthy status. Error and warning states use
 *  the same shape so the whole row family stays visually consistent. */
export const StatusPill: React.FC<{ status: 'connected' | 'reauth_required' | 'error' }> = ({
  status,
}) => {
  const meta = STATUS_META[status] ?? STATUS_META['error']!;
  return (
    <Box
      display="inline-flex"
      alignItems="center"
      gap={0.75}
      sx={{ bgcolor: meta.bg, color: meta.text, px: 1.25, py: 0.375, borderRadius: 5 }}
    >
      <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: meta.dot, flexShrink: 0 }} />
      <Typography variant="caption" fontWeight={600} lineHeight={1}>
        {meta.label}
      </Typography>
    </Box>
  );
};

/** Coarse on purpose. A sync history is scanned for "was this recent", never
 *  read for a precise duration — the exact timestamp is a tooltip away
 *  wherever this is used. */
export function timeAgo(iso?: string | Date): string {
  if (!iso) return '—';
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.round(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours} hr ago`;
  return `${Math.round(hours / 24)} d ago`;
}

/** Thousands separators on counts that routinely reach five figures — an
 *  unseparated "12480 rows read" is genuinely harder to read at a glance. */
export function formatCount(n: number): string {
  return n.toLocaleString();
}
